import { useMemo, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { weeklyOverruns } from "../lib/breaks";
import { dayLabel, fmtClock, fmtDur } from "../lib/pickerSheet";
import { slug } from "../lib/format";

const medal = ["bg-rose-500/20 text-rose-300 border-rose-500/40", "bg-amber-500/20 text-amber-300 border-amber-500/40", "bg-yellow-500/10 text-yellow-200 border-yellow-500/30"];

export const BreakOverruns = ({ users, cfg, onPicker }) => {
  const allowance = cfg.breakMin * 60;
  const { weeks, byWeek } = useMemo(() => weeklyOverruns(users, allowance), [users, allowance]);
  const [week, setWeek] = useState(null);
  const wk = weeks.includes(week) ? week : weeks[weeks.length - 1];
  if (!wk) return null;
  const rows = byWeek[wk];
  const flagged = rows.filter((r) => r.over > 0);
  const max = Math.max(1, ...rows.map((r) => r.overSec));
  return (
    <div className="panel" data-testid="break-overruns">
      <div className="flex flex-wrap items-center gap-3 p-5 border-b border-slate-800">
        <AlertTriangle className="h-5 w-5 text-rose-400" />
        <div className="mr-auto">
          <h3 className="font-display text-lg font-semibold text-slate-100">Lunch overrun alerts</h3>
          <p className="text-xs text-slate-500" data-testid="overrun-summary">{flagged.length} of {rows.length} pickers went over {cfg.breakMin} min this week · worst first</p>
        </div>
        <span className="label-mono">Week commencing</span>
        <Select value={wk} onValueChange={setWeek}>
          <SelectTrigger data-testid="overrun-week-select" className="h-9 w-[170px] bg-[#0B0F19] border-slate-700 text-slate-100 font-mono text-xs"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-80">{[...weeks].reverse().map((w) => <SelectItem key={w} value={w} className="font-mono text-xs">{dayLabel(w)} {w.slice(0, 4)}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-800">{["#", "Picker", "Days", "Overruns", "Time over", "Longest lunch", "Overrun days", ""].map((h, i) => <th key={i} className="text-left px-3 py-3 font-mono text-[11px] uppercase tracking-wider text-slate-500 whitespace-nowrap first:pl-5">{h}</th>)}</tr></thead>
          <tbody>{rows.map((r, i) => (
            <tr key={r.id} data-testid={`overrun-row-${slug(r.id)}`} onClick={() => onPicker(r.id)} className={`border-b border-slate-800/60 hover:bg-slate-800/40 cursor-pointer transition-colors ${r.over ? "" : "opacity-50"}`}>
              <td className="pl-5 py-2.5"><span className={`inline-flex h-6 w-6 items-center justify-center rounded-full border text-[11px] font-mono ${r.over ? medal[i] || "border-slate-700 text-slate-400" : "border-slate-800 text-slate-600"}`}>{i + 1}</span></td>
              <td className="px-3 text-slate-100 font-medium whitespace-nowrap">{r.id}</td>
              <td className="px-3 font-mono text-slate-400">{r.days}</td>
              <td className={`px-3 font-mono ${r.over ? "text-rose-300 font-medium" : "text-slate-500"}`} data-testid={`overrun-count-${slug(r.id)}`}>{r.over} / {r.days}</td>
              <td className="px-3 font-mono text-rose-300/90">{r.overSec ? fmtDur(r.overSec) : "—"}</td>
              <td className="px-3 font-mono text-slate-300 whitespace-nowrap">{r.longest ? `${fmtDur(r.longest)} · ${dayLabel(r.longestDay).split(",")[0]}` : "—"}</td>
              <td className="px-3 py-2">
                <div className="flex flex-wrap gap-1">{r.details.map((d) => (
                  <span key={d.day} className="font-mono text-[10px] px-1.5 py-0.5 rounded border border-rose-500/30 bg-rose-500/10 text-rose-300 whitespace-nowrap" title={`${dayLabel(d.day)}: lunch ${fmtClock(d.start)}–${fmtClock(d.start + d.dur)}`}>
                    {dayLabel(d.day).split(",")[0]} {fmtClock(d.start)} · {fmtDur(d.dur)}
                  </span>
                ))}</div>
              </td>
              <td className="px-3 w-32"><div className="h-1.5 rounded bg-slate-800"><div className="h-1.5 rounded bg-rose-500" style={{ width: `${(r.overSec / max) * 100}%` }} /></div></td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
};
